import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';

const FPV: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/');
  };

  const missions = [
    {
      id: "ALPHA_001",
      designation: "Coastal Reconnaissance",
      location: "Kelantan Beach Sector",
      altitude: "120m",
      duration: "8:45",
      status: "COMPLETED",
      footage: "4K60",
      classification: "CINEMATIC"
    },
    {
      id: "BRAVO_002", 
      designation: "Urban Patrol",
      location: "Kota Bharu Metropolitan",
      altitude: "80m",
      duration: "12:30",
      status: "COMPLETED",
      footage: "4K30",
      classification: "COMMERCIAL"
    },
    {
      id: "CHARLIE_003",
      designation: "Forest Canopy Survey",
      location: "Taman Negara Border",
      altitude: "200m",
      duration: "15:20",
      status: "IN_PROGRESS",
      footage: "4K60",
      classification: "EXPLORATION"
    },
    {
      id: "DELTA_004",
      designation: "Sunset Aerial Sequence",
      location: "Pantai Cahaya Bulan",
      altitude: "150m",
      duration: "6:12",
      status: "COMPLETED",
      footage: "4K120",
      classification: "ARTISTIC"
    }
  ];

  const droneFleet = [
    {
      model: "DJI Mini 3 Pro",
      callsign: "WHISPER",
      status: "ACTIVE",
      batteryLevel: 89,
      flightTime: "34min",
      camera: "4K/60fps",
      range: "12km"
    },
    {
      model: "Custom 5-inch Racing",
      callsign: "PHANTOM",
      status: "STANDBY", 
      batteryLevel: 95,
      flightTime: "8min",
      camera: "GoPro Hero12",
      range: "2km"
    },
    {
      model: "Beta75X Whoop",
      callsign: "SCOUT",
      status: "READY",
      batteryLevel: 100,
      flightTime: "4min",
      camera: "1080p/120fps",
      range: "200m"
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED': return 'text-green-400';
      case 'IN_PROGRESS': return 'text-yellow-400';
      case 'ACTIVE': return 'text-green-400';
      case 'STANDBY': return 'text-blue-400';
      case 'READY': return 'text-cyan-400';
      default: return 'text-gray-400';
    }
  };

  const getBatteryColor = (level: number) => {
    if (level > 75) return 'text-green-400';
    if (level > 50) return 'text-yellow-400';
    if (level > 25) return 'text-orange-400';
    return 'text-red-400';
  };

  return (
    <div className="min-h-screen bg-black text-white font-rajdhani relative overflow-hidden">
      {/* Background effects */}
      <div className="fixed inset-0 z-[1] opacity-30">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-900/20 to-black"></div>
      </div>

      {/* Military-style grid overlay */}
      <div className="fixed inset-0 z-[2] pointer-events-none">
        <div className="absolute inset-0 bg-cyber-grid opacity-5" style={{ backgroundSize: '40px 40px' }}></div>
        {/* Tactical corner markings */}
        <div className="absolute top-16 left-4 w-12 h-12 border-l-2 border-t-2 border-orange-400/60"></div>
        <div className="absolute top-16 right-4 w-12 h-12 border-r-2 border-t-2 border-orange-400/60"></div>
        <div className="absolute bottom-16 left-4 w-12 h-12 border-l-2 border-b-2 border-orange-400/60"></div>
        <div className="absolute bottom-16 right-4 w-12 h-12 border-r-2 border-b-2 border-orange-400/60"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 pt-24 pb-16 px-4">
        <BackButton onClick={handleBack} />
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="text-orange-400 font-mono text-lg mb-4 cyber-glow">
            bash@neural-core:~$ cat /tactical_ops/fpv_command.log
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            FPV_TACTICAL_OPS
          </h1>
          <p className="text-gray-400 text-xl max-w-4xl mx-auto">
            Aerial Reconnaissance • Cinematic Operations • Drone Command Center
          </p>
        </motion.div>

        <div className="max-w-7xl mx-auto">
          {/* Command Center Status */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-br from-orange-900/20 to-orange-700/10 border border-orange-400/30 p-8 rounded-lg mb-8"
          >
            <h2 className="text-orange-400 font-mono text-2xl mb-6">COMMAND_CENTER_STATUS</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-3xl text-white font-bold mb-2">156</div>
                <div className="text-gray-400">Total Flights</div>
                <div className="text-orange-400 text-xs mt-1">+12 This Month</div>
              </div>
              <div className="text-center">
                <div className="text-3xl text-green-400 font-bold mb-2">47h</div>
                <div className="text-gray-400">Flight Time</div>
                <div className="text-green-400 text-xs mt-1">Zero Incidents</div>
              </div>
              <div className="text-center">
                <div className="text-3xl text-blue-400 font-bold mb-2">3</div>
                <div className="text-gray-400">Active Drones</div>
                <div className="text-blue-400 text-xs mt-1">All Systems Go</div>
              </div>
              <div className="text-center">
                <div className="text-3xl text-purple-400 font-bold mb-2">∞</div>
                <div className="text-gray-400">Creativity Level</div>
                <div className="text-purple-400 text-xs mt-1">Limitless</div>
              </div>
            </div>
          </motion.div>

          {/* Drone Fleet Status */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-black/60 border border-green-400/30 p-6 rounded-lg"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                <h3 className="text-green-400 font-mono text-xl">DRONE_FLEET_STATUS</h3>
              </div>
              
              <div className="space-y-4">
                {droneFleet.map((drone, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 + (i * 0.1) }}
                    className="border-l-2 border-green-400/50 pl-4 hover:border-green-400 transition-colors"
                  >
                    <div className="flex justify-between items-center mb-2">
                      <div>
                        <div className="text-white font-semibold">{drone.model}</div>
                        <div className="text-gray-400 text-sm">Callsign: {drone.callsign}</div>
                      </div>
                      <div className={`${getStatusColor(drone.status)} text-xs font-mono`}>
                        {drone.status}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Battery: </span>
                        <span className={`${getBatteryColor(drone.batteryLevel)} font-bold`}>
                          {drone.batteryLevel}%
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Flight Time: </span>
                        <span className="text-white">{drone.flightTime}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Camera: </span>
                        <span className="text-white">{drone.camera}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Range: </span>
                        <span className="text-white">{drone.range}</span>
                      </div>
                    </div>
                    
                    {/* Battery indicator */}
                    <div className="mt-3">
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <motion.div
                          className={`h-2 rounded-full ${
                            drone.batteryLevel > 75 ? 'bg-green-400' :
                            drone.batteryLevel > 50 ? 'bg-yellow-400' :
                            drone.batteryLevel > 25 ? 'bg-orange-400' : 'bg-red-400'
                          }`}
                          initial={{ width: 0 }}
                          animate={{ width: `${drone.batteryLevel}%` }}
                          transition={{ delay: 1 + (i * 0.1), duration: 1.5 }}
                        />
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Mission Log */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-black/60 border border-orange-400/30 p-6 rounded-lg"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-3 h-3 bg-orange-400 rounded-full animate-pulse"></div>
                <h3 className="text-orange-400 font-mono text-xl">MISSION_LOG</h3>
              </div>
              
              <div className="space-y-4">
                {missions.map((mission, i) => (
                  <motion.div
                    key={mission.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 + (i * 0.1) }}
                    className="border-l-2 border-orange-400/50 pl-4 hover:border-orange-400 transition-colors cursor-pointer group"
                  >
                    <div className="flex justify-between items-center mb-2">
                      <div>
                        <div className="text-white font-semibold group-hover:text-orange-400 transition-colors">
                          {mission.designation}
                        </div>
                        <div className="text-gray-400 text-sm">{mission.id}</div>
                      </div>
                      <div className={`${getStatusColor(mission.status)} text-xs font-mono`}>
                        {mission.status}
                      </div>
                    </div>
                    
                    <div className="text-sm space-y-1">
                      <div>
                        <span className="text-gray-500">Location: </span>
                        <span className="text-white">{mission.location}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="text-gray-500">Alt: </span>
                          <span className="text-white">{mission.altitude}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Duration: </span>
                          <span className="text-white">{mission.duration}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="text-gray-500">Footage: </span>
                          <span className="text-cyan-400">{mission.footage}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Type: </span>
                          <span className="text-purple-400">{mission.classification}</span>
                        </div>
                      </div>
                    </div>
                    
                    <motion.div
                      className="opacity-0 group-hover:opacity-100 transition-opacity mt-2"
                      whileHover={{ scale: 1.05 }}
                    >
                      <span className="text-orange-400 text-xs cursor-pointer">
                        [VIEW_FOOTAGE] &gt;&gt;
                      </span>
                    </motion.div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Technical Specifications */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="bg-black/60 border border-blue-400/30 p-6 rounded-lg"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse"></div>
              <h3 className="text-blue-400 font-mono text-xl">TECHNICAL_SPECIFICATIONS</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <h4 className="text-white font-semibold mb-3">Flight Capabilities</h4>
                <div className="space-y-2 text-gray-300">
                  <div>• Max Altitude: 500m</div>
                  <div>• Wind Resistance: 10.7 m/s</div>
                  <div>• GPS + Visual Navigation</div>
                  <div>• Return-to-Home Auto</div>
                </div>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-3">Camera Systems</h4>
                <div className="space-y-2 text-gray-300">
                  <div>• 4K at 120fps</div>
                  <div>• 3-Axis Gimbal</div>
                  <div>• HDR & D-Log Support</div>
                  <div>• Real-time Streaming</div>
                </div>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-3">Specializations</h4>
                <div className="space-y-2 text-gray-300">
                  <div>• Cinematic Shots</div>
                  <div>• Freestyle Acrobatics</div>
                  <div>• Indoor Racing</div>
                  <div>• Long-range Surveys</div>
                </div>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-3">Safety Features</h4>
                <div className="space-y-2 text-gray-300">
                  <div>• Obstacle Avoidance</div>
                  <div>• Low Battery RTH</div>
                  <div>• Signal Loss Protocol</div>
                  <div>• Emergency Landing</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default FPV;